/**
 * Share platform registry (Release 14).
 *
 * Each platform declares how it is reached:
 *   - 'intent'  : a web URL that opens the platform's composer, prefilled
 *   - 'scheme'  : an OS URL scheme (sms:, mailto:)
 *   - 'native'  : the OS share sheet with the card image attached (mobile);
 *                 on desktop, falls back to save image + copy caption + open site
 *   - 'copyOpen': copy caption + link, then open the platform (no prefill API)
 *
 * `confirmCaption` (intent platforms that drop prefilled text, i.e. Facebook): the caption
 * is copied on tap and the sheet shows it with a "Continue to …" button before opening.
 *
 * `format` is the ShareCard format the platform receives:
 *   story (9:16), feed (4:5), link (1.91:1, used as og:image on the share page).
 */

export const SHARE_BASE_URL =
  process.env.REACT_APP_SHARE_BASE_URL || 'https://lightway-app.lightandcover.org';

export const buildShareUrl = (shareId) => `${SHARE_BASE_URL}/s/${shareId}`;

const enc = encodeURIComponent;

/** Truncate by code points (emoji-safe) at a word boundary, adding an ellipsis. */
export const fitText = (text, max) => {
  if (!text) return '';
  const chars = Array.from(text);
  if (chars.length <= max) return text;
  const cut = chars.slice(0, Math.max(0, max - 1)).join('');
  const lastSpace = cut.search(/\s\S*$/);
  const base = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${base.trimEnd()}…`;
};

/** Split caption into body and trailing hashtag line so hashtags survive trimming. */
const splitHashtags = (caption) => {
  const lines = caption.trimEnd().split('\n');
  const last = lines[lines.length - 1] || '';
  if (/^(#\w+\s*)+$/.test(last.trim())) {
    return { body: lines.slice(0, -1).join('\n').trimEnd(), tags: last.trim() };
  }
  return { body: caption.trimEnd(), tags: '' };
};

/**
 * Fit a caption into `limit` characters, keeping hashtags when there is room.
 * `reserve` is space held back for a link the platform appends.
 */
export const fitCaption = (caption, limit, reserve = 0) => {
  const budget = limit - reserve;
  if (Array.from(caption).length <= budget) return caption;
  const { body, tags } = splitHashtags(caption);
  const tagLen = tags ? Array.from(tags).length + 2 : 0;
  if (tags && budget - tagLen > 40) {
    return `${fitText(body, budget - tagLen)}\n\n${tags}`;
  }
  return fitText(body, budget);
};

const withLink = (text, url) => (url ? `${text}\n\n${url}` : text);

export const PLATFORMS = [
  {
    id: 'instagram', label: 'Instagram', monogram: 'IG', kind: 'native', format: 'story',
    color: 'linear-gradient(45deg, #F58529 0%, #DD2A7B 50%, #8134AF 80%, #515BD4 100%)',
    desktopUrl: 'https://www.instagram.com/',
  },
  {
    id: 'tiktok', label: 'TikTok', monogram: 'TT', kind: 'native', format: 'story',
    color: '#000000', desktopUrl: 'https://www.tiktok.com/upload',
  },
  {
    id: 'facebook', label: 'Facebook', monogram: 'FB', kind: 'intent', format: 'link',
    color: '#1877F2', confirmCaption: true,
    // Facebook ignores prefilled post text by policy; the caption goes through the confirm step.
    buildUrl: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
  },
  {
    id: 'x', label: 'X', monogram: 'X', kind: 'intent', format: 'link', color: '#000000',
    // X counts every link as 23 characters; hold back 24 for link + space.
    buildUrl: ({ text, url }) =>
      `https://x.com/intent/tweet?text=${enc(fitCaption(text, 280, 24))}&url=${enc(url)}`,
  },
  {
    id: 'linkedin', label: 'LinkedIn', monogram: 'LI', kind: 'intent', format: 'link',
    color: '#0A66C2',
    // Feed composer with caption + link prefilled (undocumented but widely used; the older
    // share-offsite/?url= endpoint ignores text). Posts allow 3000 characters.
    // LinkedIn unfurls the FIRST URL in the post text. If the user's reflection contains
    // links (e.g. a news article), appending ours loses the preview card to that link.
    // Prepend the share URL so ours wins the unfurl.
    buildUrl: ({ text, url }) =>
      `https://www.linkedin.com/feed/?shareActive=true&text=${enc(`${url}\n\n${fitCaption(text, 3000, url.length + 2)}`)}`,
  },
  {
    id: 'threads', label: 'Threads', monogram: 'TH', kind: 'intent', format: 'link', color: '#101010',
    buildUrl: ({ text, url }) =>
      `https://www.threads.com/intent/post?text=${enc(fitCaption(text, 500))}&url=${enc(url)}`,
  },
  {
    id: 'bluesky', label: 'Bluesky', monogram: 'BS', kind: 'intent', format: 'link', color: '#1185FE',
    buildUrl: ({ text, url }) =>
      `https://bsky.app/intent/compose?text=${enc(withLink(fitCaption(text, 300, url.length + 2), url))}`,
  },
  {
    id: 'substack', label: 'Substack', monogram: 'SS', kind: 'copyOpen', format: 'link',
    color: '#FF6719', desktopUrl: 'https://substack.com/notes', hintKey: 'share.substackHint',
  },
  {
    id: 'reddit', label: 'Reddit', monogram: 'RD', kind: 'intent', format: 'link', color: '#FF4500',
    buildUrl: ({ url, title }) =>
      `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(fitText(title, 300))}`,
  },
  {
    id: 'whatsapp', label: 'WhatsApp', monogram: 'WA', kind: 'intent', format: 'link', color: '#25D366',
    buildUrl: ({ text, url }) => `https://wa.me/?text=${enc(withLink(text, url))}`,
  },
  {
    // Slack has no prefill URL; the link unfurls into the Link card once pasted.
    id: 'slack', label: 'Slack', monogram: 'SL', kind: 'copyOpen', format: 'link',
    color: '#4A154B', desktopUrl: 'https://app.slack.com/client', hintKey: 'share.slackHint',
  },
  {
    id: 'messages', label: 'Messages', icon: 'messages', kind: 'scheme', format: 'link',
    color: '#34C759', mobileOnly: true,
    // `sms:?&body=` works on both iOS and Android.
    buildUrl: ({ text, url }) => `sms:?&body=${enc(withLink(text, url))}`,
  },
  {
    id: 'email', label: 'Email', icon: 'email', kind: 'scheme', format: 'link', color: '#5F6B7A',
    buildUrl: ({ text, url, subject }) =>
      `mailto:?subject=${enc(subject)}&body=${enc(withLink(text, url))}`,
  },
];

export const MORE_PLATFORM = {
  id: 'more', label: 'More', icon: 'more', kind: 'native', format: 'feed', color: '#E0E0E0', dark: true,
};

// ─── Device capabilities ──────────────────────────────────────────────────

export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /Android|iPhone|iPad|iPod/i.test(ua) || iPadOS;
};

/** True when the browser can put an image file into the OS share sheet. */
export const canShareFiles = () => {
  if (typeof navigator === 'undefined' || !navigator.canShare || !navigator.share) return false;
  try {
    const probe = new File([new Blob(['x'], { type: 'image/png' })], 'probe.png', { type: 'image/png' });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
};

/** Platforms visible on this device, in display order. */
export const getVisiblePlatforms = ({ mobile = isMobileDevice(), fileShare = canShareFiles() } = {}) => {
  const list = PLATFORMS.filter(p => !p.mobileOnly || mobile);
  return fileShare ? [...list, MORE_PLATFORM] : list;
};
